/**
 * The gate and the reconciliation — the two rules that keep "AI proposes,
 * estimator disposes" honest (FR11, FR13, FR15–FR17).
 *
 * Nothing here calls a model. The model returns candidates; this decides which
 * a human is even allowed to accept, and folds duplicates into one. Keeping it
 * out of the network path means the rules are testable and identical whether
 * they run on the device or the server.
 */

export type Operation = 'replace' | 'repair' | 'rni' | 'inspect';
export type WorkKind = 'body' | 'mechanical';

/** What the model returns, before any of our rules are applied. */
export interface Candidate {
  /**
   * The database row id. Optional because a candidate can exist before it is
   * saved (an in-flight read result); once persisted, this is what lets a
   * priced line point back at the evidence that produced it.
   */
  id?: string;
  zone: string;
  partLabel: string;
  damageKind?: string;
  operation: Operation;
  workKind: WorkKind;
  /** 0–100, the model's own figure. */
  confidence: number;
  /** Shot ids that support this. Empty means it was inferred, not observed. */
  evidence: string[];
  observations: string[];
  rationale?: string;
}

export type BlockReason = 'below_gate' | 'zone_not_photographed' | 'no_evidence';

export interface Proposal extends Candidate {
  /** Set when the estimator may not accept it. */
  blocked?: BlockReason;
  /** What would settle it, shown instead of an Accept button. */
  asksFor?: string;
  /**
   * What the MODEL said, when the estimator has since overruled it.
   *
   * Kept so the screen can show "the model said replace" beside a repair the
   * shop chose — and so the acceptance rate keeps measuring the model's own
   * answer rather than the shop's improvement of it.
   */
  proposedOperation?: Operation;
}

/**
 * The confidence floor. Below this a proposal is shown but cannot be accepted —
 * it asks for a photograph instead of inviting a guess.
 *
 * 50 is a product decision, not a model property. Raise it if the twenty-job
 * test shows confident inventions; lower it only if recall is the problem.
 */
export const GATE = 50;

export interface ReviewInput {
  candidates: Candidate[];
  /** Zones no usable photograph covers, from `captureGaps`. */
  uncoveredZones?: Set<string>;
  gate?: number;
}

/**
 * One damage is one proposal.
 *
 * The same dent photographed three times comes back as three candidates. Merging
 * them raises confidence rather than inflating the list — showing three rows for
 * one dent is how an estimator stops trusting the screen.
 *
 * Merge key is zone + part, deliberately not operation: the model often says
 * "repair" from one angle and "replace" from another for the same panel, and
 * that disagreement is information. The higher confidence wins the operation,
 * and both sets of observations survive.
 */
export function reconcile(candidates: Candidate[]): Candidate[] {
  const groups = new Map<string, Candidate[]>();
  for (const c of candidates) {
    const key = `${c.zone}::${c.partLabel.trim().toLowerCase()}`;
    const g = groups.get(key);
    if (g) g.push(c);
    else groups.set(key, [c]);
  }

  const out: Candidate[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) { out.push(group[0]!); continue; }

    const strongest = [...group].sort((a, b) => b.confidence - a.confidence)[0]!;
    const evidence = [...new Set(group.flatMap((c) => c.evidence))];
    const observations = [...new Set(group.flatMap((c) => c.observations))];

    // Agreement across independent photographs is genuine evidence, so raise
    // confidence — but cap the lift, because three views of the same wrong
    // reading is still one wrong reading.
    const agreeing = group.filter((c) => c.operation === strongest.operation).length;
    const lift = Math.min(8, (agreeing - 1) * 4);

    out.push({
      ...strongest,
      confidence: Math.min(99, strongest.confidence + lift),
      evidence,
      observations,
    });
  }
  return out;
}

/**
 * Apply the gate.
 *
 * Three ways a proposal becomes unacceptable, and each says something different
 * to the estimator:
 *   - no evidence at all      → inferred from impact direction, never seen
 *   - the zone was not shot   → we cannot see it, take this photograph
 *   - below the floor         → we saw something and are not sure enough
 */
export function applyGate(input: ReviewInput): Proposal[] {
  const gate = input.gate ?? GATE;
  const uncovered = input.uncoveredZones ?? new Set<string>();

  return reconcile(input.candidates).map((c): Proposal => {
    if (c.evidence.length === 0) {
      return { ...c, blocked: 'no_evidence',
        asksFor: `Nothing photographed shows this. It is inferred from the impact, not seen.` };
    }
    if (uncovered.has(c.zone)) {
      return { ...c, blocked: 'zone_not_photographed',
        asksFor: `No usable photograph covers ${c.zone.replace(/_/g, ' ')}.` };
    }
    if (c.confidence < gate) {
      return { ...c, blocked: 'below_gate',
        asksFor: `Not sure enough at ${c.confidence}%. Another photograph would settle it.` };
    }
    return c;
  });
}

/**
 * Something the model could not judge from the photographs it was given.
 *
 * NOT a lesser proposal — an honest gap, and on some jobs the most valuable
 * thing in the reply. A rear impact that creases a bumper cover often bends the
 * reinforcement bar behind it, and **no photograph taken from outside the car
 * can ever settle that**. The model saying "I cannot see this, here is the shot
 * that would help" is the difference between an estimator remembering to check
 * and an estimator finding out at teardown.
 *
 * Proven on the first real car tested: cover AND reinforcement were replaced.
 * The model proposed the cover and listed the reinforcement here.
 */
export interface CannotAssess {
  partLabel: string;
  reason: string;
  /** The shot that would settle it, in the estimator's words. */
  photographWanted: string;
}

export type Verdict = 'pending' | 'accepted' | 'rejected' | 'dismissed';

/**
 * The acceptance rate — the number the whole premise rests on.
 *
 * Blocked proposals are excluded from the denominator on purpose. A proposal the
 * system refused to offer is not a proposal the estimator rejected, and counting
 * it as one would flatter a model that hedges and punish one that is decisive.
 */
export function acceptanceRate(
  proposals: Proposal[],
  verdicts: Record<string, Verdict>,
  keyOf: (p: Proposal) => string = (p) => `${p.zone}::${p.partLabel}`,
): { accepted: number; rejected: number; offered: number; rate: number | null } {
  const offerable = proposals.filter((p) => !p.blocked);
  let accepted = 0;
  let rejected = 0;
  for (const p of offerable) {
    const v = verdicts[keyOf(p)];
    if (v === 'accepted') accepted += 1;
    else if (v === 'rejected') rejected += 1;
  }
  const decided = accepted + rejected;
  return {
    accepted,
    rejected,
    offered: offerable.length,
    // Null rather than 0 when nothing has been decided: an untouched job is not
    // a job with a 0% acceptance rate, and averaging zeros would hide the truth.
    rate: decided === 0 ? null : Math.round((accepted / decided) * 1000) / 10,
  };
}
